import {Type} from 'class-transformer';
import {BaseIdQuery, BaseListFilterWithSpecs, GLOBAL, IdCodeNameModel, IdCodeNameQuery} from 'common-shared';
import {FilterMetadata} from "primeng/api";

class CostPriceComponentPrice {
  CostPriceComponent: IdCodeNameModel;
  Price: number;
  FromDate?: Date;
  ToDate?: Date;
}

export class ResourceQuery extends IdCodeNameQuery {
  constructor(init?: Partial<ResourceQuery>) {
    super();
    return Object.assign(this, init);
  }

  KindClv: string;
  /**
   * Not selected by default
   */
  @Type(() => ResourceConnection)
  ToResourceConnections: ResourceConnection[] = [];
  /**
   * Not selected by default
   */
  @Type(() => ResourceConnection)
  FromResourceConnections: ResourceConnection[] = [];
  Sequence: number;
  StatusClv: string;
  StatusDate: string;

  static Columns() {
    return [...IdCodeNameQuery.Columns(), 'KindClv', 'Sequence', 'StatusClv', 'StatusDate'];
  }

  get DisplayName(): string {
    return BaseIdQuery.getDisplayName([this.Code, this.Name]);
  }

  get IsMachine(): boolean {
    return this.KindClv === GLOBAL.Classificators.ResourceKind.Machine;
  }

  get IsPerson(): boolean {
    return this.KindClv === GLOBAL.Classificators.ResourceKind.Person;
  }

  get FromResourceString(): string {
    return this.FromResourceConnections?.map(value => value.ToResourceId).join("|");
  }

  get ToResourceString(): string {
    return this.ToResourceConnections?.map(value => value.FromResourceId).join("|");
  }
}

export class ResourceConnection {
  ToResourceId: string;
  FromResourceId: string;
  @Type(() => Date)
  FromDate: Date;
  @Type(() => Date)
  ToDate: Date;

  TypeClv: string;
}

export class ResourceListQuery extends ResourceQuery {
  @Type(() => CostPriceComponentPrice)
  CostPriceComponentPrices: CostPriceComponentPrice[];

  get HasPriceComponents(): boolean {
    return !!this.CostPriceComponentPrices;
  }

  private get validPricesPerHour(): number[] {
    const now = new Date();
    return (this.CostPriceComponentPrices != null ? this.CostPriceComponentPrices : [])
      .filter(x => (!x.FromDate || x.FromDate < now) && (!x.ToDate || now <= x.ToDate) && !!x.Price && x.Price != 0)
      .sort((x,y) => x.CostPriceComponent?.Code?.localeCompare(y.CostPriceComponent?.Code))
      .map(x => x.Price);
  }

  private totalPricePerHourRounded(componentPrices: number[]): string {
    return this.truncateNumber(componentPrices.reduce((x,y) => x+y, 0)).toFixed(2);
  }

  get PricePerHourTotal(): number {
    return Number.parseFloat(this.totalPricePerHourRounded(this.validPricesPerHour));
  }

  get PricePerHourFormula(): string {
    const prices = this.validPricesPerHour;
    const sum = this.totalPricePerHourRounded(prices);
    const addends = prices.map(x => this.truncateNumber(x).toFixed(2));

    return addends.length == 1
      ? addends[0]
      : addends.length > 1
        ? `${addends.join(' + ')} = ${sum}`
        : '';
  }

  private truncateNumber(value: number) {
    return Number(Math.round(parseFloat(value + 'e' + 2)) + 'e-' + 2);
  }
}

export class ResourceListFilter extends BaseListFilterWithSpecs {
  KindClv: FilterMetadata | FilterMetadata[];
  IncludeToConnectionsInSelect: boolean;

  constructor(init?: Partial<ResourceListFilter>) {
    super();
    Object.assign(this, init);
  }
}
